import { Badge, Button, Group, Loader, Modal, Radio, Stack, Text } from '@mantine/core';
import { DocumentWorkflowTemplateSourceFormat } from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useTemplatePicker } from './useTemplatePicker';
import type { UseTemplatePickerArgs } from './useTemplatePicker.types';

type Props = UseTemplatePickerArgs;

export const TemplatePickerModal = (props: Props) => {
	const tMipManage = useTranslationMipManage();
	const { templates, isLoading, isAdding, selectedId, setSelectedId, add, close } =
		useTemplatePicker(props);

	return (
		<Modal
			opened={props.opened}
			onClose={close}
			title={tMipManage('settings.tax.use_template.title')}
		>
			{isLoading ? (
				<Loader />
			) : (
				<Stack gap="1rem">
					{templates.length === 0 ? (
						// Naming the tag beats an empty box: the template is published and
						// available, it just is not tagged for this section.
						<Text c="dark.0" size="sm" data-testid="template-picker-empty">
							{tMipManage('settings.tax.use_template.empty')}
						</Text>
					) : (
						<Radio.Group value={selectedId} onChange={setSelectedId}>
							<Stack gap="xs">
								{templates.map((template) => (
									<Radio
										key={template.id}
										value={template.id}
										data-testid={`template-picker-option-${template.id}`}
										label={
											<Group gap="xs">
												<Text size="sm">{template.name}</Text>
												{template.sourceFormat ===
													DocumentWorkflowTemplateSourceFormat.DOCX && (
													<Badge
														size="xs"
														variant="outline"
														color="gray.4"
													>
														{tMipManage(
															'settings.tax.use_template.word'
														)}
													</Badge>
												)}
											</Group>
										}
										description={template.description ?? undefined}
									/>
								))}
							</Stack>
						</Radio.Group>
					)}
					<Group justify="flex-end">
						<Button variant="subtle" onClick={close} type="button">
							{tMipManage('settings.tax.use_template.cancel')}
						</Button>
						<Button
							onClick={add}
							loading={isAdding}
							disabled={selectedId === null}
							data-testid="template-picker-add"
						>
							{tMipManage('settings.tax.use_template.add')}
						</Button>
					</Group>
				</Stack>
			)}
		</Modal>
	);
};
